// Lib
import React, { CSSProperties } from 'react';

// Components
import ImageModalDrawingModeContent from '../../content/ImageModalDrawingModeContent';
import ImageModalV2BackgroundModeContent from './ImageModalV2BackgroundModeContent';
import ImageModalV2CropModeContent from './ImageModalV2CropModeContent';

// Hooks
import { ImageModalV2Mode } from '../hooks/useImageModalV2Mode';

// Types
import { ImageModalV2EditModeProps } from '../imageModalV2Types';
import { RectSize } from '../../../../../../common/maths/geometry/rect/rectTypes';
import type { ImageModalV2OperationToastState } from '../toolbar/imageModalV2OperationToastTypes';
import type { ImageModalV2ErrorToastState } from '../toolbar/imageModalV2ErrorToastTypes';

type ImageModalV2EditModeContentProps = ImageModalV2EditModeProps & {
    mode: ImageModalV2Mode;
    contentStyle: CSSProperties;
    editOverlayStyle: CSSProperties;
    imageWrapperStyle: CSSProperties;
    imageBounds: RectSize;
    onEditorReadyChange?: (ready: boolean) => void;
    onOperationToastChange: (toast: ImageModalV2OperationToastState | null) => void;
    onErrorToastChange: (toast: ImageModalV2ErrorToastState | null) => void;
};

/**
 * Renders the active editor over the stable V2 image bounds.
 */
const ImageModalV2EditModeContent: React.FC<ImageModalV2EditModeContentProps> = ({
    mode,
    contentStyle,
    editOverlayStyle,
    imageWrapperStyle,
    imageBounds,
    onEditorReadyChange,
    onOperationToastChange,
    onErrorToastChange,
    ...editModeProps
}) => {
    // Drawing happens on the cropped (displayed) image, so it uses the cropped footprint.
    const croppedImageWidth = Number(imageWrapperStyle.width);
    // The crop/background editor renders the full (uncropped, possibly rotated) source image inside
    // the stable view-mode frame so switching tools does not resize the outer modal footprint.
    const captionHeight = Math.max(Number(contentStyle.height) - Number(imageWrapperStyle.height), 0);
    const editorFrameStyle = {
        width: imageBounds.width,
        height: imageBounds.height + captionHeight,
    };

    switch (mode) {
        case ImageModalV2Mode.DRAW:
            return (
                <div
                    className="ImageModalV2-edit-overlay absolute z-[1] flex items-center justify-center"
                    style={editOverlayStyle}
                >
                    <div className="ImageModalV2-drawing-overlay relative" style={contentStyle}>
                        <div
                            className="ImageModalV2-drawing-canvas absolute top-0 left-0"
                            style={{
                                width: imageWrapperStyle.width,
                                height: imageWrapperStyle.height,
                            }}
                        >
                            <ImageModalDrawingModeContent {...editModeProps} modalWidth={croppedImageWidth} />
                        </div>
                    </div>
                </div>
            );
        case ImageModalV2Mode.CROP:
            return (
                <div
                    className="ImageModalV2-edit-overlay ImageModalV2-crop-overlay absolute z-[1] flex items-center justify-center"
                    style={editOverlayStyle}
                >
                    <div
                        className="ImageModalV2-editor-overlay-frame relative flex items-center justify-center"
                        style={editorFrameStyle}
                    >
                        <ImageModalV2CropModeContent
                            {...editModeProps}
                            boundsWidth={Number(imageBounds.width)}
                            boundsHeight={Number(imageBounds.height)}
                            onEditorReadyChange={onEditorReadyChange}
                            onOperationToastChange={onOperationToastChange}
                        />
                    </div>
                </div>
            );
        case ImageModalV2Mode.BACKGROUND:
            return (
                <div
                    className="ImageModalV2-edit-overlay ImageModalV2-background-overlay absolute z-[1] flex items-center justify-center"
                    style={editOverlayStyle}
                >
                    <div
                        className="ImageModalV2-editor-overlay-frame relative flex items-center justify-center"
                        style={editorFrameStyle}
                    >
                        <ImageModalV2BackgroundModeContent
                            {...editModeProps}
                            boundsWidth={Number(imageBounds.width)}
                            boundsHeight={Number(imageBounds.height)}
                            onEditorReadyChange={onEditorReadyChange}
                            onOperationToastChange={onOperationToastChange}
                            onErrorToastChange={onErrorToastChange}
                        />
                    </div>
                </div>
            );
        case ImageModalV2Mode.VIEW:
        case ImageModalV2Mode.UPLOAD:
        case ImageModalV2Mode.DOWNLOAD:
            return null;
    }
};

export default ImageModalV2EditModeContent;
